// @flow

// We're accessing some secret/hacky APIs that are hidden behind underscores in this file.
/* eslint-disable ta-code-conventions/lithium-string-blacklist */

import * as React from 'react';
import { isElement } from 'react-is';

export { importCss, importScript } from './assets';

// $FlowFixMe: this variable may only be accessed on the server.
const imported: Set<string> = SERVER ? new Set() : undefined;

// We have to keep track of which bundles were preloaded for SSR
// This is to ensure we don't cause hydration mismatches.
export const preloaded: Set<string> = new Set(
  global?.__WEB_CONTEXT__?.pageManifest?.ssrPreloadedModules || global.__PRELOADS__ || [],
);

// We'll use this helper to enqueue things on the server to be sent down
// for initial page load on the client.
const markUsage = importer => {
  if (SERVER) {
    if (!global.__LOADED_COMPONENTS__) {
      global.__LOADED_COMPONENTS__ = [];
    }
    // $FlowFixMe: Flow doesn't know about our babel plugin.
    const key = importer.__BUNDLE_NAME__;
    if (!key && DEBUG) {
      // $FlowFixMe: Flow doesn't know about our babel plugin.
      console.error(new Error(`Invalid import name ${importer.__BUNDLE_NAME__}`));
    }
    if (!imported.has(key)) {
      imported.add(key);
      global.__LOADED_COMPONENTS__.push(key);
    }
  }
};

type LoadingState<T> = React.ComponentType<T> | React.Node;

type Importer<T> = () => Promise<T>;

// This factory will generates a Lazy-loaded version of the component it wraps
// that will "come with" the loading behavior provided to it.
const createLoader = <T: Object, P: Object>(
  shouldLoadEagerly: boolean,
  importer: () => Promise<T>,
  exportName: $Keys<T>,
  loader: LoadingState<P>,
  shouldMarkOnUsage: boolean,
) => {
  let eager = null;
  let hasDoneEagerLoad = false;
  const doEagerLoad = (): T | null => {
    if (!hasDoneEagerLoad && shouldLoadEagerly) {
      hasDoneEagerLoad = true;
      // $FlowFixMe: Flow doesn't know about our babel plugin.
      eager = preloaded.has(importer.__BUNDLE_NAME__) ? importer.__GET_SYNC__() : null;
    }
    if (eager && eager[exportName]) {
      return eager[exportName];
    }
    return null;
  };

  const Lazy = React.forwardRef(({ children, ...props }, ref) => {
    if (shouldMarkOnUsage && SERVER) {
      markUsage(importer);
    }

    const [comp, setComponent] = React.useState<T | null>((): T | null => doEagerLoad());
    React.useEffect(() => {
      let mounted = true;
      if (comp === null) {
        importer().then(pkg => {
          if (mounted) {
            // If an exportName is not provided, we fall back to requesting the 'default' package export.
            // If no default export exists, or the requested export does not exist, complain!
            if (pkg[exportName] === undefined && DEBUG) {
              console.error(
                `Aborted Import Mount: Expected package export "${exportName}" does not exist!`,
              );
              return;
            }
            // $FlowFixMe: I know you don't think we should be adding statics to react components.
            Lazy.stage = pkg[exportName].stage;
            // $FlowFixMe: I know you don't think we should be adding statics to react components.
            Lazy.load = pkg[exportName].load;
            if (typeof pkg[exportName].load === 'function') {
              // Demand load on lazy mount
              // NOTE: This does not currently support additional parameters!
              pkg[exportName].load();
            }
            setComponent((): T => pkg[exportName]);
          }
        });
      }
      return () => {
        mounted = false;
      };
    }, [comp]);

    if (comp === null) {
      if (loader && !isElement(loader) && typeof loader !== 'string') {
        // $FlowFixMe: The previous line asserts that Loader is a component type.
        return React.createElement(loader, props, children);
      }
      return ((loader: any): React.Node);
    }
    return React.createElement(comp, { ...props, ref }, children);
  });

  if (DEBUG) {
    Lazy.displayName = 'Lazy';
  }

  // $FlowFixMe: I know you don't think we should be adding statics to react components.
  Lazy.stage = (state, stage, params) => {
    const eagerComp = doEagerLoad();
    if (eagerComp && eagerComp.stage) {
      eagerComp.stage(state, stage, params);
    }
  };

  // $FlowFixMe: I know you don't think we should be adding statics to react components.
  Lazy.load = param => {
    const eagerComp = doEagerLoad();
    if (eagerComp && eagerComp.load) {
      eagerComp.load(param);
    }
  };

  return Lazy;
};

// Load the component for SSR if available.
const component = <T: Object, P: Object>(
  importer: Importer<T>,
  exportName: $Keys<T> = 'default',
  loadingState: LoadingState<P> = null,
): React.AbstractComponent<P, any> => {
  if (SERVER) {
    markUsage(importer);
  }
  return createLoader(true, importer, exportName, loadingState, false);
};

// Load the component on usage instead of import
component.onUsage = <T: Object, P: Object>(
  importer: Importer<T>,
  exportName: $Keys<T> = 'default',
  loadingState: LoadingState<P> = null,
): React.AbstractComponent<P, any> => createLoader(true, importer, exportName, loadingState, true);

// Load the component upon call to a triggering function.
component.onTrigger = <T: Object, P: Object>(
  importer: Importer<T>,
  exportName: $Keys<T> = 'default',
  loadingState: LoadingState<P> = null,
): [() => void, React.AbstractComponent<P, any>] => {
  let resolve = null;
  const prom = new Promise(resolver => {
    resolve = resolver;
  });

  return [
    () => {
      // $FlowFixMe: Flow can't establish that resolve is unconditionally set via the Promise.
      importer().then(res => resolve(res));
    },
    createLoader(false, () => prom, exportName, loadingState, false),
  ];
};

const weak = <T>(importer: Importer<T>) => {
  if (SERVER) {
    markUsage(importer);
  }

  // $FlowFixMe: Flow doesn't know about our babel plugin.
  return preloaded.has(importer.__BUNDLE_NAME__) ? importer.__GET_SYNC__() : null;
};

export { component, weak };
